import { dateToPBDate, Day, dayToString } from '@/models';
import { ServiceContainer } from '@/providers';
import { Loadable, PlannerCalendarStore } from '@/stores';
import { CourseSectionDetails } from '@buf/studyo_studyo-today-planners.bufbuild_es/studyo/today/planners/v1/resources/course_section_details_pb';
import { CourseSectionRole } from '@buf/studyo_studyo-today-planners.bufbuild_es/studyo/today/planners/v1/resources/course_section_role_pb';
import { addMonths, endOfMonth, startOfMonth, subMonths } from 'date-fns';
import { chain } from 'lodash';
import { computed, makeObservable, observable } from 'mobx';
import { BaseUpdatableViewModel, UpdatableViewModel } from '../../UpdatableViewModel';
import { UserDashboardCalendarDaySpecificDateViewModel } from './UserDashboardCalendarDaySpecificDateViewModel';

export interface UserDashboardCalendarDayViewModel extends UpdatableViewModel {
  readonly hasCalendarSyncError: boolean;
  getViewModelForDay(day: Day): UserDashboardCalendarDaySpecificDateViewModel;
  fetchDays(date: Date, force: boolean): Promise<void>;
}

export abstract class AppUserDashboardCalendarDayViewModel
  extends BaseUpdatableViewModel
  implements UserDashboardCalendarDayViewModel
{
  protected _viewModelByDay = observable.map<string, UserDashboardCalendarDaySpecificDateViewModel>();

  protected constructor(
    protected readonly _dashboardId: string,

    protected readonly _calendarStore: PlannerCalendarStore,
    protected readonly _workloadStore = ServiceContainer.services.workloadStore,
    protected readonly _workStore = ServiceContainer.services.workStore
  ) {
    super();
    makeObservable(this);
  }

  @computed
  protected get loadables(): Loadable<unknown>[] {
    return [this._workStore.workIcons];
  }

  protected abstract get courseSections(): CourseSectionDetails[];

  @computed
  get hasCalendarSyncError(): boolean {
    return this._calendarStore.error != null;
  }

  getViewModelForDay(day: Day): UserDashboardCalendarDaySpecificDateViewModel {
    const key = dayToString(day);
    const existing = this._viewModelByDay.get(key);

    if (existing != null) {
      return existing;
    }

    const viewModel = this.makeViewModelForDay(day, key);
    this._viewModelByDay.set(key, viewModel);
    return viewModel;
  }

  async fetchDays(date: Date, force: boolean): Promise<void> {
    const startDate = dateToPBDate(startOfMonth(subMonths(date, 1)));
    const endDate = dateToPBDate(endOfMonth(addMonths(date, 1)));

    const courseSections = chain(this.courseSections)
      .map((cs) =>
        cs.role === CourseSectionRole.TEACHER && cs.schoolsCourseSection != null ? cs.schoolsCourseSection : undefined
      )
      .compact()
      .value();
    const ids = courseSections.map((cs) => ({
      courseId: cs.id,
      schoolId: cs.schoolId
    }));

    await Promise.all([
      this._calendarStore.fetchDays(startDate, endDate, force),
      this._workloadStore.fetchCourseSectionsWorkloadInformationForDateRanges(ids, startDate, endDate, force)
    ]);
  }

  protected abstract makeViewModelForDay(day: Day, dayString: string): UserDashboardCalendarDaySpecificDateViewModel;
}
